import {
    ApiActionStatus,
    type DirectHotelOffer,
    type DirectHotelPrices,
    type HCOffer,
    type HotelOffer,
    type HotelProvider,
    type HotelRoom,
    type UnleashToggles,
} from '@/interfaces';
import displayValues from '../../assets/displayValues.json';
import { getHotelProvider, supportsIntegratedBooking } from './providers';
import { v4 as uuid } from 'uuid';

const mapHotelRoom = (roomName: string, price: number): HotelRoom => ({
    name: roomName,
    price: Math.ceil(price),
});

const mapInclusions = (inclusions: readonly number[]): readonly string[] =>
    inclusions.map((inclusion) => `${displayValues.inclusionTypes[inclusion]}`);

const mapTotalRate = (totalRate: number, isBundledRate: boolean, numberOfRooms: number) =>
    isBundledRate ? totalRate : totalRate * numberOfRooms;

const getProviderOverride = (defaultProvider: HotelProvider) =>
    (defaultProvider && getHotelProvider(defaultProvider.code)) || defaultProvider;

export const mapKayakOffer = (offer: HCOffer, defaultProvider: HotelProvider, numberOfRooms: number): HotelOffer => ({
    id: uuid(),
    rooms: [mapHotelRoom(offer.roomName, offer.totalRate)],
    totalRate: Math.ceil(numberOfRooms ? mapTotalRate(offer.totalRate, offer.isBundledRate, numberOfRooms) : offer.totalRate),
    isBundledRate: offer.isBundledRate,
    hasFreeCancellation: offer.hasFreeCancellation,
    canPayLater: offer.canPayLater,
    inclusions: mapInclusions(offer.inclusions ?? []),
    availableRooms: offer.availableRooms,
    provider: getProviderOverride(defaultProvider),
    encryptedParams: offer.bookUri,
    isDirect: false,
    integratedBooking: false,
    handicapRoomWeighting: 0,
});

export const getDirectOffers = (
    directPrices: DirectHotelPrices,
    hotelId: number,
    numberOfRooms: number,
    unleash: UnleashToggles,
): readonly HotelOffer[] => {
    if (
        directPrices.status === ApiActionStatus.OK &&
        Array.isArray(directPrices.hotels[hotelId]) &&
        directPrices.hotels[hotelId].length > 0 &&
        numberOfRooms === 1
    ) {
        return directPrices.hotels[hotelId].map((offer) => mapDirectOffer(offer, unleash));
    }

    return [];
};

export const mapDirectOffer = (directOffer: DirectHotelOffer, unleash: UnleashToggles): HotelOffer => ({
    id: uuid(),
    rooms: directOffer.rooms,
    provider: getHotelProvider(directOffer.provider),
    totalRate: directOffer.totalPrice,
    hasFreeCancellation: directOffer.hasFreeCancellation,
    canPayLater: !directOffer.prePaymentRequired,
    inclusions: directOffer.inclusions,
    encryptedParams: directOffer.encryptedParams,
    isDirect: true,
    integratedBooking: directOffer.integratedBooking && supportsIntegratedBooking(directOffer.provider, unleash),
    bookingParams: directOffer.bookingParams,
    loginRequired: directOffer.loginRequired,
    handicapRoomWeighting: directOffer.roomTypeCode === 'HCP' && directOffer.provider === 'THON' ? 1000000 : 0,
});

export const enrichOfferWithTrackingData = (
    offer: HotelOffer,
    country: string,
    place: string,
    checkInDate: Date | number,
    checkOutDate: Date | number,
    hotelKey: string,
    hotelId: number,
): HotelOffer => ({
    ...offer,
    country,
    place,
    checkInDate,
    checkOutDate,
    hotelKey,
    hotelId,
});
